import { apiGet, apiPost } from '../../../adapters/api-client/api';

/**
 * The learner's space (kid mode) — every read here is for one learner.
 *
 * In kid mode the server confines each of these to the active learner and
 * answers any other id as not found, so the `studentId` sent is a statement
 * of intent, not the control (STU-16-FR-06).
 */

// ── Shared envelope (backend: booking/application/learner-schedule.service.ts) ──

/** The governed join window values, with the registry stamp they were read under. */
export interface JoinPolicy {
  prejoinMinutes: number;
  graceMinutes: number;
  policyVersion: string;
}

/** STU-16-FR-03 — the zone to draw times in, and which rule chose it. */
export interface DisplayTimeZone {
  zone: string;
  /** COURSE: the zone a course's timetable is written in, before any family has one. */
  source: 'LEARNER' | 'HOUSEHOLD' | 'COURSE' | 'UTC';
}

export type JoinWindowState = 'BEFORE_WINDOW' | 'OPEN' | 'LATE' | 'ENDED';

export interface JoinWindow {
  opensAt: string;
  lateAfter: string;
  closesAt?: string;
}

export interface LearnerSession {
  bookingId: string;
  enrollmentId: string;
  seq: number;
  state: 'Confirmed' | 'Rescheduled' | string;
  scheduledStart: string;
  scheduledEnd?: string;
  courseId: string;
  courseTitle?: string;
  joinWindow: JoinWindow;
  joinState: JoinWindowState;
}

export interface LearnerClassCard {
  enrollmentId: string;
  courseId: string;
  courseTitle?: string;
  courseSubject?: string;
  teacherName?: string;
  access: 'ACTIVE' | 'PENDING';
  nextSession?: LearnerSession;
  /** STU-16-FR-05 — cancelled classes whose slot is still ahead. When only; never why. */
  cancelledSessions?: Array<{ seq: number; scheduledStart: string }>;
}

interface Envelope {
  joinPolicy: JoinPolicy;
  serverNow: string;
  timeZone: DisplayTimeZone;
}

export type LearnerClassesResponse = Envelope & { classes: LearnerClassCard[] };
export type LearnerCalendarResponse = Envelope & { calendar: LearnerSession[] };

const withLearner = (studentId: string | undefined, extra: Record<string, string> = {}) => {
  const params = new URLSearchParams(extra);
  if (studentId) params.set('studentId', studentId);
  const query = params.toString();
  return query ? `?${query}` : '';
};

/** UC-S07 "Mis clases". GET /api/v1/me/learner/classes */
export function getLearnerClasses(studentId?: string): Promise<LearnerClassesResponse> {
  return apiGet(`/api/v1/me/learner/classes${withLearner(studentId)}`);
}

/** UC-S07 learner calendar, bounded (SR-04). GET /api/v1/me/calendar?studentId= */
export function getLearnerCalendar(
  studentId: string | undefined,
  from: string,
  to: string,
): Promise<LearnerCalendarResponse> {
  return apiGet(`/api/v1/me/calendar${withLearner(studentId, { from, to })}`);
}

// ── UC-S11 progress (lane D contract) ─────────────────────────────────────────

export type LearningProfile = 'LEP_0' | 'LEP_1' | 'LEP_2' | 'LEP_3' | 'LEGACY_UNASSIGNED';
export type QualitativeState = 'STARTING' | 'DEVELOPING' | 'CONSISTENT' | 'EXTENDING';
export type ProgressMode =
  | 'HISTORY_ONLY'
  | 'SIMPLE_QUALITATIVE'
  | 'STRUCTURED_OUTCOME'
  | 'MASTERY_PROGRESSION'
  | 'SERVICE_ONLY';

export interface ProgressSnapshot {
  enrollmentId: string;
  courseId: string;
  courseTitle: string;
  learningProfile: LearningProfile;
  /** MC01-R10 — the same projection the parent reads. */
  progressMode?: ProgressMode;
  projectionVersion: string | number;
  asOf: string;
  /** Absent in SERVICE_ONLY. */
  sessionsAttended?: number;
  /** SERVICE_ONLY: classes the teacher gave. */
  sessionsDelivered?: number;
  /**
   * Classes that took place without the learner (STU-13-FR-04). Never part of
   * `sessionsAttended` or milestones, even when the class completed for
   * payment (CHG-002). Absent when there are none.
   */
  sessionsMissed?: number;
  sessionsTotal?: number;
  nextSession?: { seq: number; scheduledStart: string };
  /** UNKNOWN until a governed mapping exists (DEC-S3-P09-02). */
  currentState?: 'UNKNOWN';
  /** LEP_1: one teacher's note about one session. */
  latestObservation?: { state: QualitativeState; sequence: number; at: string };
  /** Only ever present for LEP_2 / LEP_3. */
  milestones?: { completed: number; total: number };
  latestLearnerFeedback?: { text: string; at: string };
  dataSufficiency: 'SUFFICIENT' | 'NOT_ENOUGH_HISTORY';
}

/** The shared progress projection — the same snapshot the parent sees. */
export function getLearnerProgress(studentId?: string): Promise<{ snapshots: ProgressSnapshot[] }> {
  return apiGet(`/api/v1/learning/progress${withLearner(studentId)}`);
}

// ── UC-S12 past classes (lane B contract) ─────────────────────────────────────

export type Attendance = 'PRESENT' | 'LATE' | 'NO_SHOW' | 'UNKNOWN';
export type RecordingState =
  | 'AVAILABLE'
  | 'PROCESSING'
  | 'NOT_RELEASED'
  | 'RESTRICTED'
  | 'MEDIA_UNAVAILABLE'
  | 'EXPIRED'
  | 'NOT_ENTITLED'
  | 'PLAYBACK_NOT_GRANTED'
  | 'NOT_RECORDED'
  | 'FAILED';

export interface PastClass {
  enrollmentId: string;
  courseId: string;
  courseTitle: string;
  courseVersionId?: string;
  teacherName?: string;
  seq: number;
  /** How the class ended, from the lifecycle (DEC-S3-S12-01). */
  outcome: 'COMPLETED' | 'PARTIAL' | 'CANCELLED' | 'RESCHEDULED' | 'NOT_DELIVERED';
  scheduledStart?: string;
  /** Absent on a class that was never held. */
  sessionEndedAt?: string;
  attendance: Attendance;
  recording: { state: RecordingState; availableUntil?: string };
  /**
   * MC-02 — the teacher's optional suggestion to practise after this class.
   * Read-only: no due date, no submission, no state, no grade.
   */
  suggestedPractice?: { text: string; estimatedMinutes?: number; recordedAt: string };
}

export function getPastClasses(studentId?: string): Promise<{ classes: PastClass[] }> {
  return apiGet(`/api/v1/learning/past-classes${withLearner(studentId)}`);
}

/**
 * A fresh, short-lived playback link — requested on every press of "Ver",
 * never stored, never shown (STU-15-FR-05).
 */
export function requestRecordingAccess(
  enrollmentId: string,
  seq: number,
  studentId?: string,
): Promise<{ url: string; expiresAt: string }> {
  return apiPost(
    `/api/v1/learning/recordings/${encodeURIComponent(enrollmentId)}/${seq}/access`,
    studentId ? { studentId } : {},
  );
}

// ── MC-05: the learner's in-app notices ("Mis avisos") ──

export type LearnerNoticeType =
  | 'CLASS_STARTING_SOON'
  | 'CLASS_RESCHEDULED'
  | 'CLASS_CANCELLED'
  | 'FEEDBACK_AVAILABLE';

/**
 * One notice, as the server wrote it from its governed catalogue. Only ever
 * in-app: a learner gets no email, SMS or push (MC05-R01). `link` is null when
 * the notice is stale — superseded or past its class — and a link that is
 * there is still re-checked by the screen it opens (MC05-R08, R09).
 */
export interface LearnerNotice {
  noticeId: string;
  type: LearnerNoticeType;
  title: string;
  body: string;
  sequence: number;
  startsAt?: string;
  link: string | null;
  createdAt: string;
  read: boolean;
}

/** GET /api/v1/me/learner/notifications — confined to the active learner in kid mode. */
export function getLearnerNotices(studentId?: string): Promise<{ notices: LearnerNotice[] }> {
  return apiGet(`/api/v1/me/learner/notifications${withLearner(studentId)}`);
}

/** Marks one notice read. Reading changes nothing else (MC05-R07). */
export function markLearnerNoticeRead(
  noticeId: string,
  studentId?: string,
): Promise<{ noticeId: string; read: true }> {
  return apiPost(
    `/api/v1/me/learner/notifications/${encodeURIComponent(noticeId)}/read`,
    studentId ? { studentId } : {},
  );
}

// ── UC-S07 / S12 "De qué trata" ───────────────────────────────────────────────

/**
 * STU-14-FR-05 — one of the learner's courses as the family enrolled in it:
 * the frozen course version, with nothing commercial in it.
 */
export interface LearnerCourseOverview {
  courseId: string;
  courseVersionId?: string;
  source: 'ENROLLED_VERSION' | 'CURRENT_LISTING';
  title: string;
  description?: string;
  subject?: string;
  teacherName?: string;
}

/** GET /api/v1/me/learner/courses/:courseId — only a course this learner is enrolled in. */
export function getLearnerCourse(
  courseId: string,
  studentId?: string,
): Promise<LearnerCourseOverview> {
  return apiGet(
    `/api/v1/me/learner/courses/${encodeURIComponent(courseId)}${withLearner(studentId)}`,
  );
}
